import { useEffect, useRef } from "react"
import { useThree, useFrame } from "@react-three/fiber"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
gsap.registerPlugin(ScrollTrigger)
export default function CameraAnimation() {
  const { camera, size } = useThree()
  const progressRef = useRef({})
  const cameraPos = useRef({ x: 0, y: 0, z: 4 })
  const lookAtPos = useRef({ x: 0, y: 0, z: 0 })
  const needsUpdate = useRef(true) 
  useEffect(() => {
    const aspect = size.width / size.height
    let newFov = 45 // Desktop default
    if (aspect < 1.2) {
      newFov = Math.min(60, Math.max(45, 45 + (1.2 - aspect) * 21))
    }
    camera.fov = newFov
    camera.updateProjectionMatrix()
    needsUpdate.current = true
  }, [size.width, size.height, camera])
  useFrame(() => {
    if (!needsUpdate.current) return
    camera.position.set(cameraPos.current.x, cameraPos.current.y, cameraPos.current.z)
    camera.lookAt(lookAtPos.current.x, lookAtPos.current.y, lookAtPos.current.z)
    needsUpdate.current = false
  })
  useEffect(() => {
    const sections = [
      {
        id: 1,
        section: ".section-1",
        from: { camera: [0, 0, 5], lookAt: [0, 0, 0] },
        to: { camera: [0, 0, 4], lookAt: [0, 0, 0] },
      },
      {
        id: 2,
        section: ".section-2",
        from: { camera: [0, 0, 4], lookAt: [0, 0, 0] },
        to: { camera: [0, 0, 3], lookAt: [0, 0, 0] },
      },
      {
        id: 3,
        section: ".section-3",
        from: { camera: [0, 0, 3], lookAt: [0, 0, 0] },
        to: { camera: [1.962, -0.991, 1.372], lookAt: [-1, 0.5, 0.6] },
      },
      {
        id: 4,
        section: ".section-4",
        from: { camera: [1.962, -0.991, 1.372], lookAt: [-1, 0.5, 0.6] },
        to: { camera: [1.355, -0.425, -0.139], lookAt: [-0.5, 0, 3] },
      },
      {
        id: 5,
        section: ".section-5",
        from: { camera: [1.355, -0.425, -0.139], lookAt: [-0.5, 0, 3] },
        to: { camera: [1.355, -0.425, -0.139], lookAt: [-0.5, 0, 3] },
      },
      {
        id: 6,
        section: ".section-6",
        from: { camera: [1.355, -0.425, -0.139], lookAt: [-0.5, 0, 3] },
        to: { camera: [0, -1.05, 3], lookAt: [0, -1.3, 10] },
      },
      {
        id: 7,
        section: ".section-7",
        from: { camera: [0, -50.833, 2.206], lookAt: [0, 0, 0] },
        to: { camera: [0, -50.833, 2.206], lookAt: [0, 0, 0] },
      },
      {
        id: 8,
        section: ".section-8",
        from: { camera: [0, -50.833, 2.206], lookAt: [0, 0, 0] },
        to: { camera: [0, -50.800, 2.206], lookAt: [0, 0, 0] },
      },
      {
        id: 9,
        section: ".section-9",
        from: { camera: [0, -50.800, 2.206], lookAt: [0, 0, 0] },
        to: { camera: [0, -50.500, 2.187], lookAt: [0, -15, 0] },
      },
      {
        id: 10,
        section: ".section-10",
        from: { camera: [0, -50.500, 2.187], lookAt: [0, -15, 0] },
        to: { camera: [0, -49.800, 2.135], lookAt: [0, -15, 0] },
      },
      {
        id: 11,
        section: ".section-11",
        from: { camera: [0, -49.800, 2.135], lookAt: [0, -15, 0] },
        to: { camera: [0, -49.0, 2.080], lookAt: [0, -15, 0] },
      },
      {
        id: 12,
        section: ".section-12",
        from: { camera: [0, -49.0, 2.080], lookAt: [0, -15, 0] },
        to: { camera: [0, -48.400, 2.040], lookAt: [0, -15, 0] },
      },
      {
        id: 13,
        section: ".section-13",
        from: { camera: [0, -48.400, 2.040], lookAt: [0, -15, 0] },
        to: { camera: [0, -47.800, 2], lookAt: [0, -15, 0] },
      },
      {
        id: 14,
        section: ".section-14",
        from: { camera: [0, -47.800, 2], lookAt: [0, -15, 0] },
        to: { camera: [0, -47.200, 1.98], lookAt: [0, -15, 0] },
      },
      {
        id: 15,
        section: ".section-15",
        from: { camera: [0, -47.200, 1.98], lookAt: [0, -15, 0] },
        to: { camera: [0, -47.150, 1.98], lookAt: [0, -15, 0] },
      }
    ]
    const lerp = (start, end, progress) => start + (end - start) * progress
    const updateCamera = () => {
      let activeSection = sections[0]
      let activeProgress = 0
      for (let i = sections.length - 1; i >= 0; i--) {
        const p = progressRef.current[sections[i].id] || 0
        if (p > 0) {
          activeSection = sections[i]
          activeProgress = p
          break
        }
      }
      const { from, to } = activeSection
      cameraPos.current.x = lerp(from.camera[0], to.camera[0], activeProgress)
      cameraPos.current.y = lerp(from.camera[1], to.camera[1], activeProgress)
      cameraPos.current.z = lerp(from.camera[2], to.camera[2], activeProgress)
      lookAtPos.current.x = lerp(from.lookAt[0], to.lookAt[0], activeProgress)
      lookAtPos.current.y = lerp(from.lookAt[1], to.lookAt[1], activeProgress)
      lookAtPos.current.z = lerp(from.lookAt[2], to.lookAt[2], activeProgress)
      needsUpdate.current = true 
    }
    const cameraScrollTriggers = []
    sections.forEach((config) => {
      const element = document.querySelector(config.section)
      if (!element) return
      const st = ScrollTrigger.create({
        trigger: config.section,
        start: "top bottom",
        end: "bottom bottom",
        scrub: 0.5,
        onUpdate: (self) => {
          progressRef.current[config.id] = self.progress
          updateCamera()
        },
      })
      cameraScrollTriggers.push(st)
    })
    return () => {
      cameraScrollTriggers.forEach((st) => st.kill())
    }
  }, [])
  return null
}